import { useCallback, useEffect, useState } from "react";
import { Loader2, Mic, NotebookPen, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MDEditor from "@uiw/react-md-editor";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import { useAudioRecording } from "@/hooks/uploads/useAudioRecording";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { formatDateForTitle } from "@/utils/formatting/general";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadNotesProps extends UploadComponentProps {
  researchQuery: string;
  researchTitle: string;
  onResearchQueryChange: (query: string) => void;
  onResearchTitleChange: (title: string) => void;
  /** Seeds the note title — used by the Knowledge Base "answer this" flow. */
  initialTitle?: string;
}

const UploadNotes = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  researchQuery,
  researchTitle,
  onResearchQueryChange,
  onResearchTitleChange,
  onUploadSuccess,
  onDeferredActionAdd,
  initialTitle,
}: UploadNotesProps) => {
  const [notesContent, setNotesContent] = useState<string | undefined>("");
  const [noteTitle, setNoteTitle] = useState(
    () => initialTitle?.trim() || `My Note ${formatDateForTitle()}`,
  );
  const [wasSaving, setWasSaving] = useState(false);
  const notesCreateMutation = useUploadMutation("notes", selectedAgentId, onUploadSuccess);
  const { isRecording, isTranscribing, startRecording, stopRecording } = useAudioRecording(
    (transcribedText) => {
      const currentContent = notesContent || "";
      const newContent = currentContent + (currentContent ? "\n\n" : "") + transcribedText;
      setNotesContent(newContent);
      toast.success("Audio transcribed and added to notes!");
    }
  );

  useEffect(() => {
    if (wasSaving && !notesCreateMutation.isPending) {
      setNotesContent("");
      setNoteTitle(initialTitle?.trim() || `My Note ${formatDateForTitle()}`);
      setWasSaving(false);
    }
  }, [notesCreateMutation.isPending, wasSaving, initialTitle]);

  const handleSave = useCallback(() => {
    if (!notesContent?.trim()) return;

    const content = notesContent.trim();
    const title = noteTitle;

    if (onDeferredActionAdd) {
      onDeferredActionAdd({
        type: "notes",
        data: {
          content,
          title,
        },
      });
      toast.success(`Notes will be created after genie creation`);
      setNotesContent("");
      setNoteTitle(initialTitle?.trim() || `My Note ${formatDateForTitle()}`);
    } else {
      setWasSaving(true);
      notesCreateMutation.mutate({ content, title });
      onResearchQueryChange("");
      onResearchTitleChange("");
    }
  }, [notesContent, noteTitle, notesCreateMutation, onResearchQueryChange, onResearchTitleChange, onDeferredActionAdd, initialTitle]);

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col items-center mb-6">
          <GenieIcon name="chat" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Create Text Notes</h3>
          <p className="text-sm text-gray-600 text-center">Write or paste text content directly</p>
        </div>

        <div className="py-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note Title</label>
              <Input value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="Enter note title" className="w-full" />
            </div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Content</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isTranscribing}
                className={`flex items-center gap-2 ${isRecording ? "bg-red-50 border-red-200 text-red-700" : ""}`}
              >
                {isRecording ? (
                  <>
                    <Square className="h-4 w-4" />
                    Stop Recording
                  </>
                ) : isTranscribing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Transcribing...
                  </>
                ) : (
                  <>
                    <Mic className="h-4 w-4" />
                    Voice Input
                  </>
                )}
              </Button>
            </div>
            <MDEditor
              value={notesContent}
              onChange={setNotesContent}
              preview="edit"
              hideToolbar={false}
              visibleDragbar={false}
              height={300}
              data-color-mode="light"
            />
            <Button
              onClick={handleSave}
              disabled={!notesContent?.trim() || (onDeferredActionAdd ? false : notesCreateMutation.isPending)}
              className="w-full bg-genie-primary hover:bg-genie-primary/90 text-white font-medium transition-all duration-200"
            >
              {!onDeferredActionAdd && notesCreateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <NotebookPen className="h-4 w-4 mr-2" />
                  {onDeferredActionAdd ? "Add Document" : "Create"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UploadNotes;
